import { Trans, useLingui } from "@lingui/react/macro";
import type { IntegrationCatalogResult, IntegrationSetupState } from "@rakazo/contracts";
import { Button, Disclosure, Input } from "@rakazo/ui-web";
import { Check, ExternalLink, Globe, Layers, Plug, Terminal } from "lucide-react";
import type { ComponentType, ReactNode } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { newClientId } from "../../lib/client-id";
import { connectMcpOauth } from "../../lib/mcp-connect";
import { useSpaceRpc } from "../../lib/space-rpc";
import { errorText } from "../../lib/user-error";

type Choice = "direct" | "composio" | "pipedream" | "executor";

export function IntegrationSetup({
  onDone,
  serverSetup = false,
  managedOnly = false,
  standalone = false,
  initialState,
  botId,
  assistantPicker,
  onBusyChange,
  onServerConnected,
}: {
  onDone?: () => void;
  serverSetup?: boolean;
  /** Local host settings cannot access personal MCP servers.
   * Локальні налаштування сервера не мають доступу до особистих MCP-серверів. */
  managedOnly?: boolean;
  standalone?: boolean;
  initialState?: IntegrationSetupState | null;
  botId?: string;
  assistantPicker?: (disabled: boolean) => ReactNode;
  onBusyChange?: (busy: boolean) => void;
  onServerConnected?: (id: string) => void;
}) {
  const { t } = useLingui();
  const rpc = useSpaceRpc();
  const fieldId = useId();
  const [state, setState] = useState<IntegrationSetupState | null>(initialState ?? null);
  const [selectedChoice, setChoice] = useState<Choice>(managedOnly ? "composio" : "direct");
  const choice = serverSetup ? selectedChoice : "direct";
  const [apiKey, setApiKey] = useState("");
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<IntegrationCatalogResult[]>([]);
  const [searched, setSearched] = useState(false);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [connected, setConnected] = useState<string[]>([]);
  const choices: {
    id: Choice;
    label: string;
    description: string;
    icon: ComponentType<{ className?: string }>;
  }[] = [
    {
      id: "direct",
      label: t`Direct MCP`,
      description: t`Connect a tool server directly.`,
      icon: Plug,
    },
    {
      id: "composio",
      label: "Composio",
      description: t`App connections through Composio.`,
      icon: Layers,
    },
    {
      id: "pipedream",
      label: "Pipedream",
      description: t`App connections through Pipedream Connect.`,
      icon: Globe,
    },
    {
      id: "executor",
      label: "Executor",
      description: t`Tools from your Executor server.`,
      icon: Terminal,
    },
  ];
  const selected = choices.find((item) => item.id === choice)!;
  const managed = choice === "composio" || choice === "pipedream";
  const hasCredentials = Boolean(apiKey.trim());
  const credentialsReady =
    hasCredentials && (choice !== "pipedream" || Boolean(clientId.trim() && projectId.trim()));
  const remoteResults = [
    ...new Map(
      results.flatMap((result) =>
        result.surfaces
          .filter((surface) => surface.kind === "mcp" && surface.source?.startsWith("https://"))
          .map(
            (surface) =>
              [surface.source!, { name: result.name, endpoint: surface.source! }] as const,
          ),
      ),
    ).values(),
  ];
  const configured = state?.providers.find((provider) => provider.id === choice)?.configured;

  useEffect(() => {
    if (!serverSetup || initialState) return;
    let active = true;
    void rpc.integrationSetup
      .get()
      .then((next) => {
        if (active) setState(next);
      })
      .catch(() => {
        if (active) setError(t`Could not load integrations`);
      });
    return () => {
      active = false;
    };
  }, [serverSetup, initialState]);
  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);

  async function run(action: () => Promise<void>) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (err) {
      setError(errorText(err, t`Could not connect`));
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  async function saveProvider() {
    await run(async () => {
      await rpc.integrationSetup.save(
        choice === "composio"
          ? { provider: "composio", apiKey }
          : {
              provider: "pipedream",
              clientId,
              clientSecret: apiKey,
              projectId,
              environment: "production",
            },
      );
      setApiKey("");
      setState(await rpc.integrationSetup.get());
      setNotice(t`Provider settings saved.`);
      if (!standalone) onDone?.();
    });
  }

  async function connect(name: string, url: string) {
    await run(async () => {
      const existing = (await rpc.mcp.servers.list()).find((server) => server.endpoint === url);
      const server =
        existing ??
        (await rpc.mcp.servers.create({
          slug: `integration-${newClientId().slice(0, 8)}`,
          name,
          transport: "streamable_http",
          endpoint: url,
          ...(apiKey.trim() ? { secret: apiKey.trim() } : {}),
        }));
      if (existing && apiKey.trim())
        await rpc.mcp.servers.update({ id: existing.id, secret: apiKey.trim() });
      const result = await connectMcpOauth(server.id, rpc);
      if (result === "cancelled") return;
      if (botId) await rpc.mcp.assignments.approve({ botId, serverId: server.id });
      setConnected((current) => [...new Set([...current, url])]);
      onServerConnected?.(server.id);
    });
  }

  if (serverSetup && !state?.canConfigure)
    return error ? (
      <p role="alert">{error}</p>
    ) : state ? null : (
      <p role="status">{t`Loading integrations…`}</p>
    );

  const footer =
    onDone && (!standalone || (managed && hasCredentials)) ? (
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <Button
          className="min-h-11 rounded-full px-6"
          disabled={
            busy ||
            (managed && state?.canConfigure && !credentialsReady && (!configured || hasCredentials))
          }
          onClick={() => {
            if (managed && hasCredentials) void saveProvider();
            else onDone();
          }}
        >
          {busy
            ? t`Connecting…`
            : standalone
              ? managed && hasCredentials
                ? t`Save provider`
                : t`Back to chat`
              : t`Continue`}
        </Button>
        {!standalone ? (
          <Button variant="ghost" disabled={busy} onClick={onDone}>
            <Trans>Skip</Trans>
          </Button>
        ) : null}
      </div>
    ) : null;

  return (
    <div className="space-y-7" data-testid="integration-setup">
      <header className="max-w-3xl">
        {serverSetup ? (
          <p className="mb-3 text-xs font-medium text-muted-foreground">
            <Trans>Server configuration</Trans>
          </p>
        ) : null}
        <h1 className="text-3xl font-medium tracking-tight text-foreground sm:text-4xl">
          {serverSetup ? t`Server integrations` : t`Add MCP server`}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {serverSetup
            ? t`Choose how assistants access external tools: directly or through an optional app connection service.`
            : t`MCP is a protocol for connecting tools to an assistant. Find a server or enter its address.`}
        </p>
      </header>
      <div
        className={
          standalone && serverSetup
            ? "grid items-start gap-6 md:grid-cols-[280px_minmax(0,1fr)]"
            : "space-y-5"
        }
      >
        {serverSetup ? (
          <fieldset
            aria-label={t`Integration options`}
            className={`grid gap-3 ${standalone ? "sm:grid-cols-2 md:grid-cols-1" : "sm:grid-cols-2"}`}
          >
            {choices
              .filter(({ id }) => !managedOnly || id === "composio" || id === "pipedream")
              .map(({ id, label, description, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  aria-label={label}
                  aria-describedby={`${fieldId}-${id}-description`}
                  aria-pressed={choice === id}
                  disabled={busy}
                  onClick={() => {
                    setChoice(id);
                    setApiKey("");
                    setError(null);
                    setNotice(null);
                  }}
                  className={`flex min-h-28 w-full items-start gap-3 rounded-2xl border p-4 text-start transition-colors disabled:opacity-50 ${choice === id ? "border-primary/50 bg-card shadow-sm" : "border-border/60 bg-muted/20 hover:bg-muted/50"}`}
                >
                  <Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-foreground">{label}</span>
                    <span
                      id={`${fieldId}-${id}-description`}
                      className="mt-1 block text-sm leading-relaxed text-muted-foreground"
                    >
                      {description}
                    </span>
                  </span>
                  {choice === id ? <Check className="mt-0.5 size-4 shrink-0" aria-hidden /> : null}
                </button>
              ))}
          </fieldset>
        ) : null}
        <section
          aria-labelledby={`${fieldId}-title`}
          className="min-w-0 space-y-5 rounded-2xl border border-border/60 bg-card p-5 shadow-sm sm:p-6"
        >
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span>
                {managed
                  ? t`Applies to this server`
                  : botId
                    ? t`Tools for the selected assistant`
                    : t`Tool connection`}
              </span>
              {managed && configured ? (
                <span className="inline-flex items-center gap-1.5 text-success">
                  <Check className="size-3.5" />
                  <Trans>Configured</Trans>
                </span>
              ) : null}
            </div>
            <h2 id={`${fieldId}-title`} className="text-xl font-medium">
              {selected.label}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {choice === "direct"
                ? t`MCP is a protocol for connecting tools to an assistant. Search the directory or add a server address you already have.`
                : choice === "composio"
                  ? t`Configure Composio for this server. People then connect their own app accounts through Connect apps.`
                  : choice === "pipedream"
                    ? t`Configure Pipedream Connect for this server. Use credentials from the same Pipedream project.`
                    : t`Connect tools from an Executor server. Its address must be reachable from the HrokBot server.`}
            </p>
          </div>
          {!managed ? assistantPicker?.(busy) : null}
          {managed ? (
            <>
              {state?.canConfigure ? (
                <>
                  {choice === "pipedream" ? (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label htmlFor={`${fieldId}-client-id`} className="block text-sm">
                        <Trans>Client ID</Trans>
                        <Input
                          id={`${fieldId}-client-id`}
                          className="mt-2 h-11"
                          value={clientId}
                          disabled={busy}
                          onChange={(event) => setClientId(event.target.value)}
                          autoComplete="off"
                        />
                      </label>
                      <label htmlFor={`${fieldId}-project-id`} className="block text-sm">
                        <Trans>Project ID</Trans>
                        <Input
                          id={`${fieldId}-project-id`}
                          className="mt-2 h-11"
                          value={projectId}
                          disabled={busy}
                          onChange={(event) => setProjectId(event.target.value)}
                          autoComplete="off"
                        />
                      </label>
                    </div>
                  ) : null}
                  <label htmlFor={`${fieldId}-key`} className="block text-sm">
                    {choice === "composio" ? t`API key` : t`Client secret`}
                    <Input
                      id={`${fieldId}-key`}
                      className="mt-2 h-11"
                      type="password"
                      value={apiKey}
                      disabled={busy}
                      onChange={(event) => setApiKey(event.target.value)}
                      autoComplete="new-password"
                    />
                  </label>
                  <a
                    className="inline-flex min-h-11 items-center gap-2 text-sm underline underline-offset-4"
                    href={
                      choice === "composio"
                        ? "https://dashboard.composio.dev"
                        : "https://pipedream.com/docs/connect/mcp/developers"
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Trans>Get credentials</Trans>
                    <ExternalLink aria-hidden className="size-3.5" />
                  </a>
                  {!onDone ? (
                    <Button
                      className="min-h-11 rounded-full"
                      disabled={busy || !credentialsReady}
                      onClick={() => void saveProvider()}
                    >
                      {busy ? t`Connecting…` : t`Save provider`}
                    </Button>
                  ) : null}
                </>
              ) : state && !configured ? (
                <p className="text-sm text-muted-foreground">
                  <Trans>Ask the server owner to configure this provider.</Trans>
                </p>
              ) : null}
              <div className="rounded-xl bg-muted/50 p-4 text-sm leading-relaxed text-muted-foreground">
                <span className="mb-1 block font-medium text-foreground">
                  <Trans>Next: connect an app account</Trans>
                </span>
                <Trans>
                  After saving, open Connect apps in the sidebar and authorize the account your
                  assistant should use. Server credentials do not sign in to personal accounts.
                </Trans>
              </div>
            </>
          ) : null}
          {choice === "direct" ? (
            <>
              <form
                className="flex flex-col gap-2 sm:flex-row"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!query.trim()) return;
                  void run(async () => {
                    const response = await rpc.capabilities.catalogSearch({
                      query,
                      usePublicCatalog: true,
                    });
                    setResults(response.results);
                    setSearched(true);
                  });
                }}
              >
                <Input
                  aria-label={t`Search apps`}
                  placeholder={t`Search apps`}
                  value={query}
                  disabled={busy}
                  className="h-11"
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setResults([]);
                    setSearched(false);
                  }}
                />
                <Button
                  type="submit"
                  className="min-h-11 rounded-xl"
                  disabled={busy || !query.trim()}
                >
                  {busy ? t`Searching…` : t`Search`}
                </Button>
              </form>
              {remoteResults.length ? (
                <div className="divide-y divide-border/60 rounded-xl border border-border/60 px-4">
                  {remoteResults.map((result) => (
                    <div
                      key={result.endpoint}
                      className="flex min-h-16 items-center justify-between gap-3 py-3"
                    >
                      <span className="min-w-0 break-words text-sm">{result.name}</span>
                      <Button
                        variant="outline"
                        className="min-h-11 shrink-0 rounded-full"
                        disabled={busy || connected.includes(result.endpoint)}
                        onClick={() => void connect(result.name, result.endpoint)}
                      >
                        {connected.includes(result.endpoint) ? t`Connected` : t`Connect`}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : null}
              {searched && !remoteResults.length ? (
                <p role="status" className="text-sm text-muted-foreground">
                  <Trans>No remote MCP servers found</Trans>
                </p>
              ) : null}
              <Disclosure
                className="text-sm text-muted-foreground"
                summary={<Trans>Add server URL</Trans>}
              >
                <div className="mt-3 space-y-3">
                  <Input
                    aria-label={t`Server URL`}
                    value={endpoint}
                    disabled={busy}
                    className="h-11"
                    onChange={(event) => setEndpoint(event.target.value)}
                    placeholder="https://example.com/mcp"
                  />
                  <Button
                    className="min-h-11 rounded-full"
                    disabled={busy || !endpoint.trim()}
                    onClick={() => void connect("MCP server", endpoint.trim())}
                  >
                    <Trans>Connect</Trans>
                  </Button>
                </div>
              </Disclosure>
            </>
          ) : null}
          {choice === "executor" ? (
            <>
              <label htmlFor={`${fieldId}-endpoint`} className="block text-sm">
                <Trans>Server URL</Trans>
                <Input
                  id={`${fieldId}-endpoint`}
                  className="mt-2 h-11"
                  value={endpoint}
                  disabled={busy}
                  onChange={(event) => setEndpoint(event.target.value)}
                  placeholder="https://example.com/mcp"
                />
              </label>
              <label htmlFor={`${fieldId}-token`} className="block text-sm">
                <Trans>Access token</Trans>
                <Input
                  id={`${fieldId}-token`}
                  className="mt-2 h-11"
                  value={apiKey}
                  disabled={busy}
                  onChange={(event) => setApiKey(event.target.value)}
                  type="password"
                  autoComplete="new-password"
                />
              </label>
              <Button
                className="min-h-11 rounded-full"
                disabled={busy || !endpoint.trim()}
                onClick={() => void connect("Executor", endpoint.trim())}
              >
                <Trans>Connect</Trans>
              </Button>
              <a
                href="https://executor.sh/#get-started"
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-11 items-center gap-2 text-sm underline underline-offset-4"
              >
                <Trans>Setup help</Trans>
                <ExternalLink aria-hidden className="size-3.5" />
              </a>
            </>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          {notice ? (
            <p role="status" className="flex items-center gap-2 text-sm text-success">
              <Check className="size-4" />
              {notice}
            </p>
          ) : null}
          {footer ? <div className="border-t border-border/60 pt-2">{footer}</div> : null}
        </section>
      </div>
    </div>
  );
}
